import type { StateCreator } from 'zustand'
import {
  applyNodeChanges,
  applyEdgeChanges,
  type NodeChange,
  type EdgeChange,
  type Connection,
} from '@xyflow/react'
import { logger } from '@/shared/lib/logger'
import type {
  PipelineNode,
  PipelineEdge,
  PipelineDefinition,
  HistoryEntry,
  PipelineNodeType,
} from '../../types/pipeline'
import { VALID_CONNECTIONS } from '../../types/pipeline'
import {
  getPipelineDefinitions,
  createPipelineDefinition,
  updatePipelineDefinition,
  deletePipelineDefinition as deletePipelineDefinitionApi,
} from '@/shared/lib/api'

const MAX_HISTORY = 50

export type NodeStatus = 'idle' | 'running' | 'done' | 'failed' | 'skipped'

export interface PipelineEditorSlice {
  // Canvas state
  pipelineNodes: PipelineNode[]
  pipelineEdges: PipelineEdge[]
  selectedNodeId: string | null

  // History (undo/redo)
  pipelineHistory: HistoryEntry[]
  historyIndex: number

  // Persistence
  savedPipelines: PipelineDefinition[]
  activePipelineId: number | null
  activePipelineName: string
  isDirty: boolean
  pipelinesLoading: boolean
  pipelinesError: string | null

  // Execution state
  nodeStatuses: Record<string, NodeStatus>
  nodeOutputs: Record<string, unknown>
  pipelineRunning: boolean

  // Actions — canvas
  onNodesChange: (changes: NodeChange[]) => void
  onEdgesChange: (changes: EdgeChange[]) => void
  addPipelineNode: (node: PipelineNode) => void
  removePipelineNode: (id: string) => void
  updateNodeData: (id: string, data: Record<string, unknown>) => void
  connectNodes: (connection: Connection) => void
  setSelectedNodeId: (id: string | null) => void
  isValidConnection: (connection: Connection) => boolean

  // Actions — history
  undo: () => void
  redo: () => void
  pushHistory: () => void

  // Actions — persistence
  savePipeline: () => Promise<void>
  loadPipeline: (id: number) => void
  deletePipeline: (id: number) => Promise<void>
  loadSavedPipelines: () => Promise<void>
  newPipeline: () => void
  setPipelineName: (name: string) => void
  clearCanvas: () => void

  // Actions — execution
  setNodeStatus: (id: string, status: NodeStatus) => void
  setNodeOutput: (id: string, output: unknown) => void
  setPipelineRunning: (running: boolean) => void
  resetRunState: () => void
}

export const createPipelineEditorSlice: StateCreator<
  PipelineEditorSlice,
  [],
  [],
  PipelineEditorSlice
> = (set, get) => ({
  pipelineNodes: [],
  pipelineEdges: [],
  selectedNodeId: null,
  pipelineHistory: [{ nodes: [], edges: [] }],
  historyIndex: 0,
  savedPipelines: [],
  activePipelineId: null,
  activePipelineName: 'Untitled Pipeline',
  isDirty: false,
  pipelinesLoading: false,
  pipelinesError: null,

  // Execution state
  nodeStatuses: {},
  nodeOutputs: {},
  pipelineRunning: false,

  onNodesChange: (changes) => {
    set((state) => ({
      pipelineNodes: applyNodeChanges(changes, state.pipelineNodes) as PipelineNode[],
      isDirty: true,
    }))
  },

  onEdgesChange: (changes) => {
    set((state) => ({
      pipelineEdges: applyEdgeChanges(changes, state.pipelineEdges) as PipelineEdge[],
      isDirty: true,
    }))
  },

  addPipelineNode: (node) => {
    set((state) => ({
      pipelineNodes: [...state.pipelineNodes, node],
      isDirty: true,
    }))
    get().pushHistory()
  },

  removePipelineNode: (id) => {
    set((state) => ({
      pipelineNodes: state.pipelineNodes.filter((n) => n.id !== id),
      pipelineEdges: state.pipelineEdges.filter((e) => e.source !== id && e.target !== id),
      selectedNodeId: state.selectedNodeId === id ? null : state.selectedNodeId,
      isDirty: true,
    }))
    get().pushHistory()
  },

  updateNodeData: (id, data) => {
    set((state) => ({
      pipelineNodes: state.pipelineNodes.map((n) =>
        n.id === id ? { ...n, data: { ...n.data, ...data } } : n
      ) as PipelineNode[],
      isDirty: true,
    }))
  },

  connectNodes: (connection) => {
    if (!get().isValidConnection(connection)) return
    const edge: PipelineEdge = {
      id: `e-${connection.source}-${connection.target}`,
      source: connection.source!,
      target: connection.target!,
      sourceHandle: connection.sourceHandle ?? undefined,
      targetHandle: connection.targetHandle ?? undefined,
      type: 'pipeline',
    }
    set((state) => ({
      pipelineEdges: [...state.pipelineEdges, edge],
      isDirty: true,
    }))
    get().pushHistory()
  },

  setSelectedNodeId: (id) => set({ selectedNodeId: id }),

  isValidConnection: (connection) => {
    const { pipelineNodes, pipelineEdges } = get()
    const sourceNode = pipelineNodes.find((n) => n.id === connection.source)
    const targetNode = pipelineNodes.find((n) => n.id === connection.target)
    if (!sourceNode || !targetNode) return false

    // No self-connections
    if (sourceNode.id === targetNode.id) return false

    // No duplicate connections
    const exists = pipelineEdges.some(
      (e) => e.source === connection.source && e.target === connection.target
    )
    if (exists) return false

    // Validate source → target type pairing
    const sourceType = sourceNode.type as PipelineNodeType
    const targetType = targetNode.type as PipelineNodeType
    const allowed = VALID_CONNECTIONS[sourceType]
    return allowed ? allowed.includes(targetType) : false
  },

  // ── History ─────────────────────────────────────────────────────────────

  pushHistory: () => {
    const { pipelineNodes, pipelineEdges, pipelineHistory, historyIndex } = get()
    const entry: HistoryEntry = {
      nodes: JSON.parse(JSON.stringify(pipelineNodes)),
      edges: JSON.parse(JSON.stringify(pipelineEdges)),
    }
    const trimmed = pipelineHistory.slice(0, historyIndex + 1)
    const newHistory = [...trimmed, entry].slice(-MAX_HISTORY)
    set({
      pipelineHistory: newHistory,
      historyIndex: newHistory.length - 1,
    })
  },

  undo: () => {
    const { historyIndex, pipelineHistory } = get()
    if (historyIndex <= 0) return
    const newIndex = historyIndex - 1
    const entry = pipelineHistory[newIndex]
    set({
      pipelineNodes: entry.nodes,
      pipelineEdges: entry.edges,
      historyIndex: newIndex,
      isDirty: true,
    })
  },

  redo: () => {
    const { historyIndex, pipelineHistory } = get()
    if (historyIndex >= pipelineHistory.length - 1) return
    const newIndex = historyIndex + 1
    const entry = pipelineHistory[newIndex]
    set({
      pipelineNodes: entry.nodes,
      pipelineEdges: entry.edges,
      historyIndex: newIndex,
      isDirty: true,
    })
  },

  // ── Persistence ─────────────────────────────────────────────────────────

  savePipeline: async () => {
    const { pipelineNodes, pipelineEdges, activePipelineId, activePipelineName } = get()

    try {
      if (activePipelineId) {
        // Update existing
        const updated = await updatePipelineDefinition(activePipelineId, {
          name: activePipelineName,
          nodes: pipelineNodes as unknown[],
          edges: pipelineEdges as unknown[],
        })
        set((state) => ({
          savedPipelines: state.savedPipelines.map((p) =>
            p.id === activePipelineId
              ? { ...p, name: updated.name, nodes: updated.nodes as PipelineNode[], edges: updated.edges as PipelineEdge[], updated_at: updated.updated_at }
              : p
          ),
          isDirty: false,
        }))
      } else {
        // Create new
        const created = await createPipelineDefinition({
          name: activePipelineName,
          nodes: pipelineNodes as unknown[],
          edges: pipelineEdges as unknown[],
        })
        const definition: PipelineDefinition = {
          id: created.id,
          name: created.name,
          description: created.description,
          nodes: created.nodes as PipelineNode[],
          edges: created.edges as PipelineEdge[],
          created_at: created.created_at,
          updated_at: created.updated_at,
        }
        set((state) => ({
          savedPipelines: [definition, ...state.savedPipelines],
          activePipelineId: created.id,
          isDirty: false,
        }))
      }
    } catch (error) {
      logger.error('Failed to save pipeline', { error })
      set({ pipelinesError: error instanceof Error ? error.message : 'Failed to save' })
    }
  },

  loadPipeline: (id) => {
    const pipeline = get().savedPipelines.find((p) => p.id === id)
    if (!pipeline) return
    set({
      pipelineNodes: pipeline.nodes,
      pipelineEdges: pipeline.edges,
      activePipelineId: pipeline.id,
      activePipelineName: pipeline.name,
      isDirty: false,
      selectedNodeId: null,
      pipelineHistory: [{ nodes: pipeline.nodes, edges: pipeline.edges }],
      historyIndex: 0,
    })
  },

  deletePipeline: async (id) => {
    try {
      await deletePipelineDefinitionApi(id)
      set((state) => ({
        savedPipelines: state.savedPipelines.filter((p) => p.id !== id),
        activePipelineId: state.activePipelineId === id ? null : state.activePipelineId,
      }))
    } catch (error) {
      logger.error('Failed to delete pipeline', { error })
    }
  },

  loadSavedPipelines: async () => {
    set({ pipelinesLoading: true, pipelinesError: null })
    try {
      const apiPipelines = await getPipelineDefinitions()
      const pipelines: PipelineDefinition[] = apiPipelines.map((p) => ({
        id: p.id,
        name: p.name,
        description: p.description,
        nodes: p.nodes as PipelineNode[],
        edges: p.edges as PipelineEdge[],
        created_at: p.created_at,
        updated_at: p.updated_at,
      }))
      set({ savedPipelines: pipelines, pipelinesLoading: false })
    } catch (error) {
      logger.error('Failed to load pipelines', { error })
      set({
        pipelinesError: error instanceof Error ? error.message : 'Failed to load pipelines',
        pipelinesLoading: false,
      })
    }
  },

  newPipeline: () => {
    set({
      pipelineNodes: [],
      pipelineEdges: [],
      activePipelineId: null,
      activePipelineName: 'Untitled Pipeline',
      isDirty: false,
      selectedNodeId: null,
      pipelineHistory: [{ nodes: [], edges: [] }],
      historyIndex: 0,
    })
  },

  setPipelineName: (name) => set({ activePipelineName: name, isDirty: true }),

  clearCanvas: () => {
    set({
      pipelineNodes: [],
      pipelineEdges: [],
      selectedNodeId: null,
      isDirty: true,
    })
    get().pushHistory()
  },

  // ── Execution ─────────────────────────────────────────────────────────

  setNodeStatus: (id, status) => {
    set((state) => ({
      nodeStatuses: { ...state.nodeStatuses, [id]: status },
    }))
  },

  setNodeOutput: (id, output) => {
    set((state) => ({
      nodeOutputs: { ...state.nodeOutputs, [id]: output },
    }))
  },

  setPipelineRunning: (running) => set({ pipelineRunning: running }),

  resetRunState: () => {
    set({
      nodeStatuses: {},
      nodeOutputs: {},
      pipelineRunning: false,
    })
  },
})
